import { useEffect, useRef, useState } from "react";
import { apiGet, apiPost, prettifyApiError } from "@/shared/api";
import type { Order } from "../types";
import { ACTION_ICON_CLS, ActionButton, type ActionButtonTone } from "./ActionButton";
import { IconPrint } from "./icons";

/** Stato di un lavoro di stampa come lo legge il backend (``stampa/service.py``).
 *  ``esito_ignoto``: l'agente l'ha presa e non ha mai detto com'e' andata. */
export type StatoStampa =
  | "in_coda"
  | "in_stampa"
  | "stampata"
  | "errore"
  | "scaduta"
  | "esito_ignoto";

interface LavoroStampa {
  id: number;
  stato: StatoStampa;
  etichette: number;
  errore: string;
}

interface StatoAgente {
  collegato: boolean;
}

const FINALI: ReadonlySet<StatoStampa> = new Set(["stampata", "errore", "scaduta", "esito_ignoto"]);
/** L'agente chiede lavoro ogni ~3 s: 1,5 s di passo vede l'esito appena c'e'. */
const PASSO_MS = 1500;
/** Oltre, si smette di chiedere e si dice perche' (PC spento o agente fermo). */
const ATTESA_MAX_MS = 25_000;

export function accodaStampaLdv(ref: string): Promise<LavoroStampa> {
  return apiPost<LavoroStampa>(`/api/stampa/ldv/${encodeURIComponent(ref)}`);
}

const leggiLavoro = (id: number) => apiGet<LavoroStampa>(`/api/stampa/lavori/${id}`);
const leggiAgente = () => apiGet<StatoAgente>("/api/stampa/agente");

/** Testo dell'esito, uno per stato: e' quello che Davide legge sotto il tasto. */
export function messaggioStampa(lavoro: LavoroStampa, agenteCollegato: boolean | null): string {
  switch (lavoro.stato) {
    case "stampata":
      return lavoro.etichette > 1 ? `Stampate ${lavoro.etichette} etichette` : "Stampata";
    case "errore":
      return `Zebra: ${lavoro.errore || "errore"}`;
    case "scaduta":
      return "Non stampata: il PC della Zebra non l'ha presa in tempo";
    case "esito_ignoto":
      return "Esito non arrivato: guarda la stampante prima di ristampare";
    default:
      return agenteCollegato === false
        ? "In attesa: il PC della Zebra sembra spento"
        : "In coda per la Zebra…";
  }
}

function tonoDi(stato: StatoStampa | null): ActionButtonTone {
  if (stato === "stampata") return "emerald";
  if (stato === "errore" || stato === "scaduta" || stato === "esito_ignoto") return "rose";
  return "slate";
}

/** Tasto «Zebra»: stampa la LDV gia' generata sulla termica di BS COM.
 *
 *  Il server non vede la stampante (sta nella LAN dell'ufficio): il clic accoda
 *  lo ZPL, un agente sul PC lo manda alla porta 9100. Qui si segue il lavoro
 *  finche' ha un esito, perche' «ogni pulsante deve dare un esito» (Davide) — e
 *  un'etichetta che non esce va detta, non indovinata. */
export function StampaZebraButton({
  order,
  variant = "tile",
}: {
  order: Order;
  /** ``inline`` nella testata della pagina ordine (vedi ActionButton). */
  variant?: "tile" | "inline";
}) {
  const [busy, setBusy] = useState(false);
  const [lavoro, setLavoro] = useState<LavoroStampa | null>(null);
  const [agente, setAgente] = useState<boolean | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const vivo = useRef(true);
  useEffect(() => {
    vivo.current = true;
    return () => {
      vivo.current = false;
    };
  }, []);

  const segui = async (primo: LavoroStampa) => {
    const fine = Date.now() + ATTESA_MAX_MS;
    let corrente = primo;
    while (vivo.current && !FINALI.has(corrente.stato) && Date.now() < fine) {
      await new Promise((r) => setTimeout(r, PASSO_MS));
      corrente = await leggiLavoro(primo.id);
      if (vivo.current) setLavoro(corrente);
    }
    if (vivo.current && !FINALI.has(corrente.stato)) {
      const stato = await leggiAgente();
      if (vivo.current) setAgente(stato.collegato);
    }
  };

  const stampa = () => {
    setBusy(true);
    setErrore(null);
    setLavoro(null);
    setAgente(null);
    void accodaStampaLdv(order.id)
      .then(async (nuovo) => {
        setLavoro(nuovo);
        await segui(nuovo);
      })
      .catch((err: unknown) => setErrore(prettifyApiError(err)))
      .finally(() => {
        if (vivo.current) setBusy(false);
      });
  };

  const esito = errore ?? (lavoro ? messaggioStampa(lavoro, agente) : null);
  const negativo = errore !== null || tonoDi(lavoro?.stato ?? null) === "rose";
  return (
    <span className="relative inline-flex">
      <ActionButton
        tone={errore ? "rose" : tonoDi(lavoro?.stato ?? null)}
        icon={<IconPrint className={ACTION_ICON_CLS} />}
        label={variant === "inline" ? "Stampa Zebra" : "Zebra"}
        variant={variant}
        busy={busy}
        ariaLabel={`Stampa LDV ordine ${order.id} sulla Zebra`}
        title={esito ?? "Stampa la LDV sulla stampante Zebra dell'ufficio"}
        onClick={(e) => {
          e.stopPropagation();
          stampa();
        }}
      />
      {esito && (
        <span
          role={negativo ? "alert" : "status"}
          data-testid="stampa-zebra-esito"
          className={`absolute -bottom-1 right-0 z-10 translate-y-full whitespace-nowrap text-[10px] ${negativo ? "text-red" : "text-ink-2"}`}
        >
          {esito}
        </span>
      )}
    </span>
  );
}
